// src/services/messagingService.js
import { supabase } from '../lib/supabase';
import { logEvent } from '../lib/activityLogger';
import { createId } from '../utils/createId';
import { withSupabaseQueryTimeout } from '../lib/supabaseTimeout';
import { getDisplayNames } from './memberDisplayService';

// Allowed MIME types (must match Edge Function allowlist)
export const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/tiff',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
];

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;   // 10 MB per file
export const MAX_TOTAL_SIZE_BYTES = 25 * 1024 * 1024;  // 25 MB per thread

// Sanitize filename: allow only safe characters
export function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').substring(0, 200);
}

export const messagingService = {
  // ── Threads ──────────────────────────────────────────────

  /** Fetch threads visible to current user, paginated */
  getThreads: async ({ limit = 50, before = null, hoaId = null } = {}) => {
    const { data, error } = await withSupabaseQueryTimeout(
      () => {
        let query = supabase
          .from('message_threads')
          .select(`
            id, type, subject, status, acc_details, created_at, updated_at,
            created_by
          `)
          .order('updated_at', { ascending: false })
          .limit(limit);

        if (hoaId) query = query.eq('hoa_id', hoaId);
        if (before) query = query.lt('updated_at', before);
        return query;
      },
      'Loading message threads',
      30000
    );
    if (error) throw error;
    const threads = data || [];
    // Names come from get_member_display_names; members cannot read other profiles.
    const names = await getDisplayNames(threads.map(t => t.created_by));
    return threads.map(t => ({ ...t, creator: names.get(t.created_by) || null }));
  },

  /** Create a new thread (and first post in one transaction via two inserts) */
  createThread: async ({ type, subject, body, accDetails = null, hoaId = null }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    if (!hoaId) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('hoa_id')
        .eq('id', user.id)
        .single();
      hoaId = profile?.hoa_id;
    }

    if (!hoaId) throw new Error('No HOA associated with user');

    const { data: thread, error: threadError } = await supabase
      .from('message_threads')
      .insert({
        hoa_id: hoaId,
        created_by: user.id,
        type,
        subject,
        status: type === 'acc' ? 'submitted' : 'open',
        acc_details: accDetails,
      })
      .select()
      .single();

    if (threadError) throw threadError;

    const { error: postError } = await supabase
      .from('message_posts')
      .insert({ thread_id: thread.id, author_id: user.id, body });

    if (postError) throw postError;

    logEvent('message_sent', { thread_id: thread.id, type, subject });
    
    // Automatically mark read for the creator so it doesn't appear unread in their own UI
    await messagingService.markRead(thread.id, user.id);
    window.dispatchEvent(new Event('messages_read'));
    return thread;
  },

  /** Update thread status (acc_members and staff only — enforced by RLS) */
  updateStatus: async (threadId, status) => {
    const { error } = await supabase
      .from('message_threads')
      .update({ status })
      .eq('id', threadId);
    if (error) throw error;
    logEvent('acc_status_changed', { thread_id: threadId, status });
  },

  // ── Posts ─────────────────────────────────────────────────

  /** Fetch all posts for a thread */
  getPosts: async (threadId, { limit = 100 } = {}) => {
    const { data, error } = await supabase
      .from('message_posts')
      .select(`
        id, author_id, body, created_at
      `)
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true })
      .limit(limit);
    if (error) throw error;
    const posts = data || [];
    const names = await getDisplayNames(posts.map(p => p.author_id));
    return posts.map(p => ({ ...p, author: names.get(p.author_id) || null }));
  },

  /** Post a reply */
  addPost: async (threadId, body) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('message_posts')
      .insert({ thread_id: threadId, author_id: user.id, body });
    if (error) throw error;

    logEvent('message_replied', { thread_id: threadId });
    
    // Automatically mark read for the replier
    await messagingService.markRead(threadId, user.id);
    window.dispatchEvent(new Event('messages_read'));
  },

  // ── Reads ─────────────────────────────────────────────────

  /** Mark a thread as read for current user */
  markRead: async (threadId, overrideUserId = null) => {
    const { data: { user } } = await supabase.auth.getUser();
    const targetId = overrideUserId || user?.id;
    if (!targetId) return;

    const { error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('message_reads')
        .upsert({ thread_id: threadId, user_id: targetId, last_read_at: new Date().toISOString() },
                 { onConflict: 'thread_id,user_id' }),
      'Marking message thread read',
      30000
    );
    if (error) throw error;
  },

  /** Get map of thread IDs the user has read to their last_read_at timestamp */
  getReadThreadIds: async (overrideUserId = null) => {
    const { data: { user } } = await supabase.auth.getUser();
    const targetId = overrideUserId || user?.id;
    if (!targetId) return new Map();

    const { data, error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('message_reads')
        .select('thread_id, last_read_at')
        .eq('user_id', targetId),
      'Loading message read state',
      30000
    );
    if (error) throw error;

    const map = new Map();
    (data || []).forEach(r => {
       map.set(r.thread_id, new Date(r.last_read_at).getTime());
    });
    return map;
  },

  /** Get exact number of unread threads visible to current real user.
   *  Only counts threads where someone ELSE posted since your last read —
   *  never counts threads you created but haven't received a reply on.
   */
  getUnreadThreadCount: async (user, effectiveRole, hoaId = null) => {
    try {
      if (!user) return 0;

      const [allThreads, readMap] = await Promise.all([
        messagingService.getThreads({ hoaId }),
        messagingService.getReadThreadIds(user.id),
      ]);
      const filteredThreads = allThreads.filter(thread => {
        const isWelcome = thread.acc_details?.is_welcome === true;

        if (isWelcome) {
          const mappedId = thread.acc_details?.target_user_id || thread.acc_details?.recipient_id;
          if (mappedId) return mappedId === user.id;

          const firstName = user.name ? user.name.split(' ')[0] : 'xx_no_match';
          if (thread.subject?.includes(firstName)) return true;

          return thread.created_by === user.id;
        }

        if (effectiveRole === 'member') {
          return thread.type === 'announcement' || thread.created_by === user.id;
        }

        return true;
      });

      let count = 0;
      for (const thread of filteredThreads) {
        // Threads you originated are only unread if someone else replied after your last read
        let isSentByMe = thread.created_by === user.id;
        if (thread.acc_details?.is_welcome === true) isSentByMe = false;
        const lastRead = readMap.get(thread.id);

        if (isSentByMe) {
          // Never unread on first send. Only unread if there's been a reply (updated_at > created_at by >2s)
          // AND that update happened after your last read.
          const createdMs = new Date(thread.created_at).getTime();
          const updatedMs = new Date(thread.updated_at).getTime();
          const hasReply = updatedMs - createdMs > 2000;
          if (!hasReply) continue; // no reply yet — skip, never show as unread
          const isUnread = !lastRead || updatedMs > lastRead;
          if (isUnread) count++;
        } else {
          // Inbound thread — standard unread logic
          const isUnread = !lastRead || new Date(thread.updated_at).getTime() > lastRead;
          if (isUnread) count++;
        }
      }
      return count;
    } catch (e) {
      console.error('Failed to get unread thread count:', e);
      return 0;
    }
  },

  // ── Attachments ───────────────────────────────────────────

  /** Upload files to Storage, validate via Edge Function, insert metadata rows */
  uploadAttachments: async (threadId, files) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const results = [];

    for (const file of files) {
      // Validate MIME client-side first (UX gate only)
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        throw new Error(`File type not allowed: ${file.name}`);
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new Error(`File too large (max 10 MB): ${file.name}`);
      }

      const safeName = sanitizeFilename(file.name);
      const storagePath = `${threadId}/${createId()}-${safeName}`;

      // Upload to Storage
      const { error: uploadError } = await supabase.storage
        .from('acc-attachments')
        .upload(storagePath, file, { contentType: file.type });

      if (uploadError) throw uploadError;

      // Validate via Edge Function (magic byte check)
      const { data: { session } } = await supabase.auth.getSession();
      const validateRes = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/validate-attachment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ storage_path: storagePath, declared_mime_type: file.type }),
        }
      );

      if (!validateRes.ok) {
        const err = await validateRes.json();
        throw new Error(err.error || 'File validation failed');
      }

      // Insert metadata
      const { error: metaError } = await supabase
        .from('message_attachments')
        .insert({
          thread_id: threadId,
          storage_path: storagePath,
          filename: safeName,
          mime_type: file.type,
          size_bytes: file.size,
          uploaded_by: user.id,
        });

      if (metaError) throw metaError;

      results.push({ storagePath, filename: safeName, mimeType: file.type, size: file.size });
    }

    return results;
  },

  /** Get attachments for a thread */
  getAttachments: async (threadId) => {
    const { data, error } = await supabase
      .from('message_attachments')
      .select('id, filename, mime_type, size_bytes, storage_path, created_at')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /** Get a 15-minute signed URL for an attachment */
  getSignedUrl: async (storagePath) => {
    const { data, error } = await supabase.storage
      .from('acc-attachments')
      .createSignedUrl(storagePath, 900); // 900 seconds = 15 minutes
    if (error) throw error;
    return data.signedUrl;
  },

  // ── Realtime ──────────────────────────────────────────────

  /** Subscribe to new posts on any visible thread.
   *  Uses Supabase Realtime v2 (postgres_changes) syntax.
   *  RLS is enforced server-side.
   */
  subscribeToNewPosts: (callback) => {
    return supabase
      .channel('message_posts_inserts')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'message_posts' },
        callback
      )
      .subscribe();
  },

  unsubscribe: (channel) => {
    supabase.removeChannel(channel);
  },
};
